/**
 * CEPI Agents — SSE hook for /api/v2/workstreams/{id}/stream (plan Task 11.3).
 *
 * Modelled on ``hooks/useResearchStream.ts:96-97,116-122,176-180``:
 * ``fetch()`` + ``response.body.getReader()``, ``event:``/``data:`` frame
 * parsing, ``AbortController`` cleanup.
 *
 * **``EventSource`` is deliberately NOT used.** It cannot set request headers,
 * and the BFF (``apiProxy``) requires an ``Authorization: Bearer`` header plus
 * the impersonation headers — and the reconnect contract below requires a
 * ``Last-Event-ID`` header we control. ``EventSource`` gives us none of those.
 *
 * The reconnect contract (backend ``agent_workstream_routes.stream_workstream``,
 * spec §6.7):
 *   - Every connection opens with an ``event: snapshot`` frame built from
 *     PERSISTED state, and its ``id:`` is the workstream's ``sse_seq``. The
 *     snapshot always precedes the tail, INCLUDING on reconnect — that
 *     handshake is what closes a retention gap without inventing events.
 *   - On reconnect we send ``Last-Event-ID: <highest seq we have seen>``. The
 *     server replays STRICTLY GREATER seqs; sending it is what prevents both a
 *     duplicate and a gap. Dropping the header would silently re-deliver
 *     everything after the snapshot's seq.
 *   - ``event: done`` carries the terminal status the DB holds; it ends the
 *     stream for good and we do not reconnect after it.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import type {
  StepSummary,
  WorkstreamEvent,
  WorkstreamSnapshot,
} from '../types/workstream';
import { isDemoWorkstream, useDemoReplayStream } from '../demo/cepiDemoReplay';

// SSE requires the Gen 2 Cloud Run proxy (apiStreamProxy). The Gen 1 apiProxy
// buffers the ENTIRE response body before sending (see functions/src/index.ts
// — "Gen 1 Cloud Functions buffer the entire response body"), so a stream sent
// through it delivers no snapshot/status/step/planning frame until the whole
// connection closes — the page then looks frozen (plan never appears, running
// state never advances) until a manual reload. Prefer VITE_STREAMING_API_URL
// exactly as hooks/useStreamingSearch.ts does; the VITE_API_URL/apiProxy
// fallback stays only for local dev where nothing actually streams.
const API_URL = import.meta.env['VITE_STREAMING_API_URL'] ||
  import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

/** Default backoff before re-opening a stream that ended without ``done``. */
export const DEFAULT_RECONNECT_DELAY_MS = 2000;

export type WorkstreamConnectionStatus =
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'error';

export interface WorkstreamStreamState {
  /** Authoritative persisted state from the most recent ``snapshot`` frame. */
  snapshot: WorkstreamSnapshot | null;
  /** Tail envelopes received after the snapshot, in arrival order. */
  events: WorkstreamEvent[];
  /** Highest ``id:`` seen — exactly what a reconnect sends as Last-Event-ID. */
  lastEventId: number | null;
  connection: WorkstreamConnectionStatus;
  /** Terminal status from the ``done`` frame; null until the stream ends. */
  terminalStatus: string | null;
  error: string | null;
  /** HTTP status of a failed stream open (404 = unreadable, 401 = anon). */
  httpStatus: number | null;
}

export interface UseWorkstreamStreamOptions {
  /** Milliseconds to wait before re-opening a dropped stream. */
  reconnectDelayMs?: number;
  /** Set false to leave the stream closed (e.g. before the id is known). */
  enabled?: boolean;
}

const INITIAL_STATE: WorkstreamStreamState = {
  snapshot: null,
  events: [],
  lastEventId: null,
  connection: 'connecting',
  terminalStatus: null,
  error: null,
  httpStatus: null,
};

interface ParsedFrame {
  id: string | null;
  event: string;
  data: string;
}

/** Parse one ``\n\n``-delimited SSE frame into its id/event/data parts. */
function parseFrame(part: string): ParsedFrame | null {
  let id: string | null = null;
  let event = 'message';
  let data = '';
  for (const line of part.split('\n')) {
    if (line.startsWith('id: ')) {
      id = line.slice(4).trim();
    } else if (line.startsWith('event: ')) {
      event = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      data = line.slice(6);
    }
  }
  if (!data) return null;
  return { id, event, data };
}

/**
 * Merge a ``step`` envelope's payload into the snapshot's step list so live
 * progress and a reloaded page render from the SAME shape. The snapshot is
 * still the authority: a step the snapshot does not know about is appended,
 * never invented from nothing, and a payload without an ``ordinal`` is ignored.
 */
function applyStepEvent(
  snapshot: WorkstreamSnapshot | null,
  payload: Record<string, unknown>,
): WorkstreamSnapshot | null {
  if (!snapshot) return snapshot;
  const ordinal = payload['ordinal'];
  if (typeof ordinal !== 'number') return snapshot;
  const existing = snapshot.steps.find((s) => s.ordinal === ordinal);
  const merged = { ...(existing ?? {}), ...payload } as StepSummary;
  const steps = existing
    ? snapshot.steps.map((s) => (s.ordinal === ordinal ? merged : s))
    : [...snapshot.steps, merged].sort((a, b) => a.ordinal - b.ordinal);
  return { ...snapshot, steps };
}

export function useWorkstreamStream(
  workstreamId: string | null | undefined,
  options: UseWorkstreamStreamOptions = {},
): WorkstreamStreamState {
  const { reconnectDelayMs = DEFAULT_RECONNECT_DELAY_MS, enabled = true } = options;
  const [state, setState] = useState<WorkstreamStreamState>(INITIAL_STATE);
  // Demo replay (cepiDemoReplay.ts): the demo id never touches the network.
  const isDemo = isDemoWorkstream(workstreamId);
  const demoState = useDemoReplayStream(isDemo ? workstreamId ?? null : null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The resume point survives across reconnects — it is the whole contract.
  const lastEventIdRef = useRef<number | null>(null);
  const doneRef = useRef(false);

  const connect = useCallback(async () => {
    if (!workstreamId || !enabled || isDemo) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isResume = lastEventIdRef.current !== null;
    setState((prev) => ({
      ...prev,
      connection: isResume ? 'reconnecting' : 'connecting',
      error: null,
    }));

    try {
      const token = await auth.currentUser?.getIdToken();
      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      // Resume STRICTLY after what we already hold. Omitting this on a
      // reconnect re-delivers the tail we already rendered.
      if (lastEventIdRef.current !== null) {
        headers['Last-Event-ID'] = String(lastEventIdRef.current);
      }
      Object.assign(headers, getImpersonationHeaders());

      const response = await fetch(
        `${API_URL}/v2/workstreams/${workstreamId}/stream`,
        { headers, signal: controller.signal },
      );

      if (!response.ok) {
        // 404 is "you may not read this" (existence is never leaked); 401 is
        // unauthenticated. Neither is retryable, so we stop rather than spin.
        setState((prev) => ({
          ...prev,
          connection: 'error',
          error: `Stream failed: ${response.status}`,
          httpStatus: response.status,
        }));
        doneRef.current = true;
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      setState((prev) => ({ ...prev, connection: 'open', httpStatus: response.status }));

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;
          const frame = parseFrame(part);
          if (!frame) continue;

          let parsed: unknown;
          try {
            parsed = JSON.parse(frame.data);
          } catch {
            continue; // skip unparseable frames, as the research precedent does
          }

          const seq = frame.id === null ? null : Number(frame.id);
          if (seq !== null && Number.isFinite(seq)) {
            if (lastEventIdRef.current === null || seq > lastEventIdRef.current) {
              lastEventIdRef.current = seq;
            }
          }

          switch (frame.event) {
            case 'snapshot': {
              const snap = parsed as WorkstreamSnapshot;
              setState((prev) => ({
                ...prev,
                snapshot: snap,
                connection: 'open',
                lastEventId: lastEventIdRef.current,
              }));
              break;
            }
            case 'status':
            case 'step':
            case 'drift':
            case 'error':
            case 'planning': {
              const envelope = parsed as WorkstreamEvent;
              setState((prev) => {
                // Defence in depth against a duplicate replay: the server
                // emits strictly-greater seqs, but a client that renders the
                // same seq twice is indistinguishable from progress.
                if (prev.events.some((e) => e.seq === envelope.seq)) return prev;
                const next: WorkstreamStreamState = {
                  ...prev,
                  events: [...prev.events, envelope],
                  lastEventId: lastEventIdRef.current,
                };
                // `planning` falls through both branches below (they are
                // keyed on `frame.event`) and never touches `snapshot` — that
                // separation from `status`/`step` is the whole point of
                // giving it its own event type (spec §4.1).
                if (frame.event === 'step') {
                  next.snapshot = applyStepEvent(prev.snapshot, envelope.payload || {});
                } else if (frame.event === 'status') {
                  const status = (envelope.payload || {})['status'];
                  if (typeof status === 'string' && prev.snapshot) {
                    const reason = (envelope.payload || {})['action_required_reason'];
                    next.snapshot = {
                      ...prev.snapshot,
                      status,
                      action_required_reason:
                        status === 'awaiting_approval' && reason && typeof reason === 'object'
                          ? reason as WorkstreamSnapshot['action_required_reason']
                          : null,
                    };
                  }
                }
                return next;
              });
              break;
            }
            case 'done': {
              doneRef.current = true;
              const status = (parsed as { status?: string }).status ?? null;
              setState((prev) => ({
                ...prev,
                connection: 'closed',
                terminalStatus: status,
                // The `done` frame's status is the DB's terminal truth.
                snapshot: prev.snapshot && status
                  ? {
                    ...prev.snapshot,
                    status,
                    action_required_reason:
                      status === 'awaiting_approval'
                        ? prev.snapshot.action_required_reason
                        : null,
                  }
                  : prev.snapshot,
              }));
              controller.abort();
              return;
            }
            default:
              break;
          }
        }
      }

      // The body ended without a `done` frame — the connection dropped or the
      // server's max-connection window elapsed. Re-open from `Last-Event-ID`.
      if (!doneRef.current && !controller.signal.aborted) {
        setState((prev) => ({ ...prev, connection: 'reconnecting' }));
        timerRef.current = setTimeout(() => { void connect(); }, reconnectDelayMs);
      }
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      if (e?.name === 'AbortError') return;
      setState((prev) => ({
        ...prev,
        connection: 'error',
        error: e?.message ?? 'Stream error',
      }));
    }
  }, [workstreamId, enabled, isDemo, reconnectDelayMs]);

  useEffect(() => {
    lastEventIdRef.current = null;
    doneRef.current = false;
    setState(INITIAL_STATE);
    void connect();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [connect]);

  return demoState ?? state;
}

export default useWorkstreamStream;
